/**
 * Message Item Component
 * Individual message display with actions
 */

import { useState, useRef, useCallback } from 'react';
import type { Message } from '@/types/api.types';
import { DELETED_USER_ID } from '@/constants/system-users';
import { useAuthStore } from '@/store/auth.store';
import { useUpdateMessage, useDeleteMessage, useHideLinkPreview } from '@/hooks/useMessages';
import {
  usePinMessage,
  useUnpinMessage,
  useIsMessagePinned,
} from '@/hooks/usePinnedMessages';
import { useMentionReadTracking } from '@/hooks/useMentionReadTracking';
import { useAppStore } from '@/store/app.store';
import { useBookmarks } from '@/hooks/useBookmarks';
import Avatar from '@/components/common/Avatar';
import AttachmentDisplay from '@/components/attachments/AttachmentDisplay';
import ReactionBar from '@/components/reactions/ReactionBar';
import HeartReaction from '@/components/reactions/HeartReaction';
import ReplyButton from '@/components/messages/ReplyButton';
import ViewThreadButton from '@/components/messages/ViewThreadButton';
import MoreMenu from '@/components/messages/MoreMenu';
import { PollMessage } from '@/components/polls';
import { AudioPlayer } from '@/components/voice-messages';
import LinkPreview from '@/components/messages/LinkPreview';
import { MentionIndicator } from '@/components/mentions/MentionIndicator';
import { BookmarkButton } from '@/components/bookmarks';
import ForwardedMessageCard from '@/components/messages/ForwardedMessageCard';
import UserProfilePopover from '@/components/common/UserProfilePopover';
import ForwardMessageModal from '@/components/messages/ForwardMessageModal';
import { linkifyText, stripMentionHtml, serializeChannelReferences } from '@/utils/linkify.util';

interface MessageItemProps {
  message: Message;
  channelId?: string | null;
  onReply?: (message: Message) => void;
  isGrouped?: boolean;
}

const MessageItem = ({ message, channelId, onReply, isGrouped }: MessageItemProps) => {
  const { user: currentUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content);
  const [showActions, setShowActions] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const hideActionsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks mentions extracted from content when entering edit mode
  const editMentionMapRef = useRef<Map<string, string>>(new Map());
  const editChannelMapRef = useRef<Map<string, string>>(new Map());

  const showActionsEnter = useCallback(() => {
    if (hideActionsTimeout.current) {
      clearTimeout(hideActionsTimeout.current);
      hideActionsTimeout.current = null;
    }
    setShowActions(true);
  }, []);

  const showActionsLeave = useCallback(() => {
    hideActionsTimeout.current = setTimeout(() => setShowActions(false), 200);
  }, []);

  const updateMessageMutation = useUpdateMessage();
  const deleteMessageMutation = useDeleteMessage();
  const hideLinkPreviewMutation = useHideLinkPreview();
  const pinMessageMutation = usePinMessage();
  const unpinMessageMutation = useUnpinMessage();
  const { bookmarks } = useBookmarks();

  const { unreadMentions, activeChannelId, activeDMThreadId } = useAppStore();

  const isOwnMessage = currentUser?.id === message.user_id;
  const isPinned = useIsMessagePinned(channelId ?? null, message.id);
  const canPin = channelId && message.channel_id; // Only allow pinning in channels, not DMs
  // BookmarkButton handles its own bookmark state internally
  void bookmarks;

  // Check if current user is mentioned in this message
  const conversationId = activeChannelId || activeDMThreadId || '';
  const messageMentions = unreadMentions[conversationId] || [];
  const currentMention = messageMentions.find((m) => m.message_id === message.id);
  const hasMention = !!currentMention;
  const isMentionRead = currentMention?.is_read ?? true;

  // Auto-read tracking for mentions
  const mentionRef = useMentionReadTracking({
    messageId: message.id,
    hasMention,
    isRead: isMentionRead,
    mentionId: currentMention?.id,
    threshold: 0.5,
  });

  const handleEdit = () => {
    // Extract mentions and channel references, build maps, then strip HTML for textarea display
    const mentionRegex = /<mention data-user-id="([a-f0-9-]{36})">(@[^<]+)<\/mention>/gi;
    const map = new Map<string, string>();
    let match;
    while ((match = mentionRegex.exec(message.content)) !== null) {
      map.set(match[2], match[1]); // "@DisplayName" → userId
    }
    editMentionMapRef.current = map;

    const channelRegex = /<channel data-channel-id="([a-f0-9-]{36})">(#[^<]+)<\/channel>/gi;
    const channelMap = new Map<string, string>();
    while ((match = channelRegex.exec(message.content)) !== null) {
      channelMap.set(match[2], match[1]); // "#ChannelName" → channelId
    }
    editChannelMapRef.current = channelMap;

    setIsEditing(true);
    setEditedContent(stripMentionHtml(message.content));
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(message.content);
    editMentionMapRef.current.clear();
    editChannelMapRef.current.clear();
  };

  const handleSaveEdit = async () => {
    if (!editedContent.trim()) return;

    // Reconstruct mention HTML from plain @DisplayName text
    let finalContent = editedContent.trim();
    for (const [displayText, userId] of editMentionMapRef.current.entries()) {
      if (finalContent.includes(displayText)) {
        finalContent = finalContent.replaceAll(
          displayText,
          `<mention data-user-id="${userId}">${displayText}</mention>`
        );
      }
    }

    // Reconstruct channel HTML from plain #ChannelName text
    finalContent = serializeChannelReferences(finalContent, editChannelMapRef.current);

    try {
      await updateMessageMutation.mutateAsync({
        messageId: message.id,
        content: finalContent,
      });
      setIsEditing(false);
      editMentionMapRef.current.clear();
      editChannelMapRef.current.clear();
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Сигурен ли си, че искаш да изтриеш това съобщение?')) {
      return;
    }

    try {
      await deleteMessageMutation.mutateAsync(message.id);
    } catch {
      // Error handled by mutation
    }
  };

  const handleTogglePin = async () => {
    if (!channelId) return;

    try {
      if (isPinned) {
        await unpinMessageMutation.mutateAsync({
          channelId,
          messageId: message.id,
        });
      } else {
        await pinMessageMutation.mutateAsync({
          channelId,
          messageId: message.id,
        });
      }
    } catch {
      // Error handled by mutation
    }
  };

  const handleScrollToReply = () => {
    if (!message.reply_to_message_id) return;

    // Find the original message element by ID
    const targetElement = document.getElementById(`message-${message.reply_to_message_id}`);

    if (targetElement) {
      // Scroll to the message with smooth animation
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Add highlight effect
      const messageElement = targetElement;
      messageElement.classList.add('bg-blue-100');

      // Remove highlight after 2 seconds
      setTimeout(() => {
        messageElement.classList.remove('bg-blue-100');
      }, 2000);
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
      return date.toLocaleTimeString('bg-BG', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else {
      return date.toLocaleDateString('bg-BG', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  const formatTimeShort = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('bg-BG', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      ref={mentionRef}
      id={`message-${message.id}`}
      data-conversation-id={conversationId}
      className={`group flex items-start space-x-3 ${isGrouped ? 'py-0.5' : 'py-2'} px-4 transition-colors duration-100 relative ${
        hasMention && !isMentionRead
          ? 'bg-amber-50 dark:bg-amber-900/10 border-l-2 border-amber-500 pl-3'
          : 'hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary/70'
      }`}
      onMouseEnter={showActionsEnter}
      onMouseLeave={showActionsLeave}
    >
      {/* Mention Indicator (badge on left side) */}
      {hasMention && (
        <div className="absolute -left-2 top-2">
          <MentionIndicator variant="badge" isRead={isMentionRead} />
        </div>
      )}

      {/* Avatar or hover-timestamp placeholder for grouped messages */}
      {isGrouped ? (
        <div className="w-10 flex-shrink-0 flex items-center justify-center">
          <span className="text-[11px] text-gray-400 dark:text-dark-text-muted opacity-0 group-hover:opacity-100 transition-opacity select-none">
            {formatTimeShort(message.created_at)}
          </span>
        </div>
      ) : !isOwnMessage && !message.users.is_bot ? (
        <UserProfilePopover user={message.users}>
          <Avatar user={message.users} size="md" />
        </UserProfilePopover>
      ) : (
        <Avatar user={message.users} size="md" />
      )}

      {/* Message Content */}
      <div className="flex-1 min-w-0">
        {/* Forwarded Message Card (if this is a forwarded message) */}
        {message.forwarded_from_message_id && (
          <ForwardedMessageCard
            originalMessage={message.forwarded_from_message}
            sourceChannel={message.forwarded_from_channel}
            sourceDmThread={message.forwarded_from_dm_thread}
          />
        )}

        {/* Reply Context (if this is a reply) */}
        {message.reply_to_message && (
          <div
            className="mb-1 pl-3 border-l-2 border-primary-300 dark:border-primary-500/30 text-xs text-gray-600 dark:text-dark-text-secondary cursor-pointer hover:border-primary-500 hover:text-primary-600 transition-colors"
            onClick={handleScrollToReply}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleScrollToReply();
              }
            }}
          >
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              <span className="font-medium">{message.reply_to_message.users?.display_name}</span>
            </div>
            <div className="truncate text-gray-500 dark:text-dark-text-muted mt-0.5">
              {stripMentionHtml(message.reply_to_message.content)}
            </div>
          </div>
        )}

        {/* Header — hidden for grouped follow-up messages */}
        {!isGrouped && (
          <div className="flex items-baseline space-x-2">
            {message.users.id === DELETED_USER_ID ? (
              <span className="text-sm font-semibold italic text-gray-400 dark:text-gray-500">
                {message.users.display_name}
              </span>
            ) : !isOwnMessage && !message.users.is_bot ? (
              <UserProfilePopover user={message.users}>
                <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
                  {message.users.display_name}
                </span>
              </UserProfilePopover>
            ) : (
              <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
                {message.users.display_name}
              </span>
            )}
            {message.users.is_bot && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                BOT
              </span>
            )}
            <span className="text-[11px] text-gray-400 dark:text-dark-text-muted">
              {formatTime(message.created_at)}
            </span>
            {message.is_edited && (
              <span className="text-xs text-gray-400 dark:text-dark-text-muted italic">(редактирано)</span>
            )}
          </div>
        )}
        {isGrouped && message.is_edited && (
          <span className="text-xs text-gray-400 dark:text-dark-text-muted italic">(редактирано)</span>
        )}

        {/* Message Body */}
        {isEditing ? (
          <div className="mt-1 space-y-2">
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (editedContent.trim() && editedContent !== stripMentionHtml(message.content)) {
                    handleSaveEdit();
                  }
                }
                if (e.key === 'Escape') {
                  handleCancelEdit();
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-secondary dark:bg-dark-bg-input dark:text-dark-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              autoFocus
            />
            <div className="flex space-x-2">
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={
                  updateMessageMutation.isPending ||
                  !editedContent.trim() ||
                  editedContent === stripMentionHtml(message.content)
                }
              >
                {updateMessageMutation.isPending ? 'Запазване...' : 'Запази'}
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-800 dark:hover:text-dark-text-primary"
                disabled={updateMessageMutation.isPending}
              >
                Отказ
              </button>
            </div>
          </div>
        ) : (
          <>
            {message.content && (
              <div className={`${isGrouped ? '' : 'mt-1'} text-sm text-gray-900 dark:text-dark-text-primary whitespace-pre-wrap break-words`}>
                {linkifyText(message.content)}
              </div>
            )}

            {/* Attachments */}
            {message.message_attachments && message.message_attachments.length > 0 && (() => {
              // Separate audio and non-audio attachments
              const audioAttachments = message.message_attachments?.filter(
                att => att.mime_type.startsWith('audio/')
              ) || [];
              const otherAttachments = message.message_attachments?.filter(
                att => !att.mime_type.startsWith('audio/')
              ) || [];

              return (
                <>
                  {/* Audio attachments (voice messages) */}
                  {audioAttachments.map(attachment => (
                    <div key={attachment.id} className="mt-2">
                      <AudioPlayer
                        src={`/api/attachments/${attachment.id}/download`}
                        fileName={attachment.file_name}
                      />
                    </div>
                  ))}

                  {/* Other attachments (files, images, etc.) */}
                  {otherAttachments.length > 0 && (
                    <AttachmentDisplay attachments={otherAttachments} />
                  )}
                </>
              );
            })()}

            {/* Polls — message carries only the poll id; full poll fetched by id */}
            {message.polls && message.polls.length > 0 && (
              <PollMessage pollId={message.polls[0].id} />
            )}

            {/* Link Previews */}
            {message.message_links && message.message_links.length > 0 && (
              <div className="mt-2 space-y-2">
                {message.message_links
                  .filter(link => !link.hidden)
                  .map(link => (
                    <LinkPreview
                      key={link.id}
                      link={link}
                      isAuthor={isOwnMessage}
                      onHide={(linkId) => {
                        hideLinkPreviewMutation.mutate({
                          messageId: message.id,
                          linkId,
                          hidden: true,
                        });
                      }}
                    />
                  ))}
              </div>
            )}
          </>
        )}

        {/* Reactions Section - Display only */}
        <ReactionBar
          messageId={message.id}
          reactions={message.message_reactions || []}
          displayOnly={true}
        />
      </div>

      {/* Action Buttons */}
      {showActions && !isEditing && (
        <div
          className="absolute -top-3 right-2 animate-action-bar"
          onMouseEnter={showActionsEnter}
          onMouseLeave={showActionsLeave}
        >
          <div className="flex items-center bg-white dark:bg-dark-bg-elevated border border-gray-200 dark:border-dark-border-primary rounded-lg shadow-sm">
            {/* Heart reaction trigger */}
            <HeartReaction
              messageId={message.id}
              reactions={message.message_reactions || []}
            />

            <div className="w-px h-5 bg-gray-200 dark:bg-dark-border-secondary" />

            {/* Reply */}
            {onReply && <ReplyButton message={message} onReply={onReply} />}

            {/* 2. View Thread */}
            <ViewThreadButton message={message} />

            {/* 3. Pin (visible to all channel members, only in channels) */}
            {canPin && (
            <button
              onClick={handleTogglePin}
              className={`p-2 rounded-lg transition-colors ${
                isPinned
                  ? 'text-blue-600 hover:text-blue-700'
                  : 'text-gray-500 hover:text-blue-600'
              }`}
              title={isPinned ? 'Откачи' : 'Закачи'}
              disabled={
                pinMessageMutation.isPending || unpinMessageMutation.isPending
              }
            >
              <svg
                className="w-4 h-4"
                fill={isPinned ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
            </button>
          )}

          {/* 5. Bookmark */}
          <BookmarkButton messageId={message.id} size="sm" />

          {/* 6. More Menu (Edit, Delete, Copy, etc.) */}
          <MoreMenu
            message={message}
            isOwnMessage={isOwnMessage}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onForward={() => setShowForwardModal(true)}
          />
          </div>
        </div>
      )}

      {/* Forward Message Modal */}
      {showForwardModal && (
        <ForwardMessageModal
          message={message}
          onClose={() => setShowForwardModal(false)}
        />
      )}
    </div>
  );
};

export default MessageItem;
